// src/features/projects/views/ProjectDashboard.jsx

import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useProject from "../hooks/useProject";
import { openProtectedPdf } from "../../../utils/pdf/openProtectedPdf";
import BaseModalApp from "../../../components/modals/app-modals/BaseModalApp";
import CatalogLaunchShell from "../../../catalog/launch/CatalogLaunchShell";
import { useCatalogLaunch } from "../../../catalog/launch/CatalogLaunchContext";
import {
  ProgramScopedLocaleProvider,
  resolveScopedTranslation,
  useProgramScopedLocale
} from "../../program-localization/ProgramScopedLocaleContext";
import ProgramScopedLocaleSelector from "../../program-localization/ProgramScopedLocaleSelector";
import ReleasedAssessmentReview, { formatScorePoints } from "../../engine/capabilities/released-assessment-review/ReleasedAssessmentReview";
import { getProgramScopedLocaleConfig } from "../../program-localization/programScopedLocaleConfig";
import ScrollToTopApp from "../../../utils/ScrollToTopApp";
import {
  readResolvedStepDenialPresentation,
  readResolvedStepState,
} from "../../engine/utils/resolvedStepState";
import {
  renderExactTokenizedCopy,
  temporalCopyTokens,
} from "../../program-presentation/programTemporalTokenPresentation";
import { projectParticipationContextLabel } from "../utils/projectPresentation";

function ExactCopyBlock({ blockId, text }) {
  if (!blockId || !text) return null;
  return (
    <div className="projects-exact-copy" data-copy-block={blockId}>
      {text.split("\n\n").map(paragraph => <p key={paragraph}>{paragraph}</p>)}
    </div>
  );
}

const samePresentationFact = (left, right) =>
  Boolean(left && right && String(left).trim() === String(right).trim());

function LearnerSummary({ isCompleted, baseGradeResult }) {
  const baseGradeAvailable = baseGradeResult?.availability === "available" &&
    Number.isFinite(baseGradeResult.points) && baseGradeResult.maxPoints === 40;
  if (!isCompleted && !baseGradeAvailable) return null;

  return (
    <section aria-label="Learner summary" className="projects-learner-summary">
      {isCompleted && <div className="projects-learner-summary__item projects-progress">
        <span className="projects-learner-summary__label">Status</span>
        <strong className="projects-learner-summary__value">Completed</strong>
      </div>}

      {baseGradeAvailable && <div className="projects-learner-summary__item projects-base-grade">
        <span className="projects-learner-summary__label">Grade</span>
        <strong className="projects-learner-summary__value">{formatScorePoints(baseGradeResult.points)} <span>/40</span></strong>
        <span className="projects-learner-summary__meta">Base grade available</span>
      </div>}
    </section>
  );
}

const G3_PRACTICE_DAY_PROJECTION = "building-your-clinical-ear.practice-days@3";
const sameDayWarning = dayCount => `You have already listened today. This session counts toward your four listening sessions, but it will not add a new day. Full credit for spacing needs 3 separate days, and you have listened on ${dayCount} so far.`;

function SpacingProgress({ phaseCompleted, stage }) {
  if (!stage || stage.missingCalendarContext) return null;
  const required = stage.requirement?.minDistinctDays;
  if (!Number.isInteger(required) || required < 1) return null;
  const completedDays = Math.min(stage.distinctPracticeDays || 0, required);
  const dayCount = stage.distinctPracticeDays || 0;
  const text = dayCount === 0
    ? "Spread this stage’s sessions across at least 3 separate days to earn full credit for spacing."
    : dayCount === 1
      ? "Listened on 1 of 3 separate days. Listen on 2 more days to earn full credit for spacing."
      : dayCount === 2
        ? "Listened on 2 of 3 separate days. Listen on 1 more day to earn full credit for spacing."
        : "Listened on 3 separate days. Full credit for spacing earned.";

  return (
    <div className="projects-spacing-progress">
      <p>{text}</p>
      <div
        aria-label={`${completedDays} of ${required} spacing days completed`}
        className="projects-spacing-markers"
        role="img"
      >
        {Array.from({ length: required }, (_, index) => (
          <span aria-hidden="true" key={index}>{index < completedDays ? "●" : "○"}</span>
        ))}
      </div>
    </div>
  );
}

export default function ProjectDashboard({ projectSlug = null }) {
  const { t } = useTranslation();
  const { slug: routeSlug } = useParams();
  const slug = projectSlug || routeSlug;
  const navigate = useNavigate();
  const auth = useAuth();
  const { source, catalogEntry } = useCatalogLaunch();
  const isCatalogLaunch =
    source === "catalog" && Boolean(catalogEntry);

  const [showEthicsModal, setShowEthicsModal] = useState(false);

  const apiUrl =
    process.env.REACT_APP_API_URL ||
    "http://localhost:5001/api";

  const {
    project,
    programs,
    membership,
    effectiveAccess,
    progress,
    loading
  } = useProject({
    slug,
    token: auth.user?.token,
    apiUrl
  });

  const [certificationStatus, setCertificationStatus] = useState(null);
  const intervalRef = useRef(null);

  // ============================================================
  // 🔥 SHOW ETHICS MODAL
  // ============================================================

  useEffect(() => {
    if (!project?._id) return;

    const seenKey = `ethics_seen_${project._id}`;
    const alreadySeen = localStorage.getItem(seenKey);
    const justJoined = localStorage.getItem("just_joined_project");

    if (!alreadySeen || justJoined === project._id.toString()) {
      setShowEthicsModal(true);
      localStorage.removeItem("just_joined_project");
    }
  }, [project]);

  const handleAcceptEthics = async () => {
    try {
      await fetch(
        `${apiUrl}/projects/${project._id}/accept-consent`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user?.token}`
          }
        }
      );

      const key = `ethics_seen_${project._id}`;
      localStorage.setItem(key, "true");

      setShowEthicsModal(false);
    } catch (err) {
      console.error("Consent error", err);
    }
  };

  // ============================================================
  // 🔥 POLLING
  // ============================================================

  useEffect(() => {
    if (!project?._id || !auth.user?.token) return;

    let isMounted = true;

    async function fetchStatus() {
      try {
        const res = await fetch(
          `${apiUrl}/certifications/summary-status?projectId=${project._id}`,
          {
            headers: {
              Authorization: `Bearer ${auth.user?.token}`
            }
          }
        );

        if (!res.ok) return;

        const data = await res.json();

        if (!isMounted) return;

        setCertificationStatus(data);

        if (data.status !== "processing" && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } catch (err) {
        console.error("❌ certification status error:", err);
      }
    }

    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 4000);

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [project?._id, apiUrl, auth.user?.token]);

  // ============================================================
  // GUARDS
  // ============================================================

  if (loading) return <p>{t("loading")}</p>;
  if (!project) return <p>{t("project_not_found")}</p>;

  const scopedProgram =
    programs.find((program) => getProgramScopedLocaleConfig({
      projectSlug: slug,
      programSlug: program.programSlug
    })) || null;

  return (
    <ProgramScopedLocaleProvider
      projectSlug={slug}
      programSlug={scopedProgram?.programSlug}
      userCommunicationLanguage={auth.user?.communicationLanguage}
    >
      <ProjectDashboardContent
        apiUrl={apiUrl}
        auth={auth}
        certificationStatus={certificationStatus}
        handleAcceptEthics={handleAcceptEthics}
        isCatalogLaunch={isCatalogLaunch}
        membership={membership}
        effectiveAccess={effectiveAccess}
        navigate={navigate}
        progress={progress}
        programs={programs}
        project={project}
        showEthicsModal={showEthicsModal}
        slug={slug}
      />
    </ProgramScopedLocaleProvider>
  );
}

function ProjectDashboardContent({
  apiUrl,
  auth,
  certificationStatus,
  handleAcceptEthics,
  isCatalogLaunch,
  membership,
  effectiveAccess,
  navigate,
  progress,
  programs,
  project,
  showEthicsModal,
  slug
}) {
  const { t: appT } = useTranslation();
  const scopedLocale = useProgramScopedLocale();
  const scopedT = scopedLocale.programT;
  const [releasedReview, setReleasedReview] = useState(null);
  const [releasedReviewStageTitle, setReleasedReviewStageTitle] = useState("Stage 4");
  const [releasedReviewLoading, setReleasedReviewLoading] = useState(false);
  const [releasedReviewError, setReleasedReviewError] = useState("");
  const [pendingSpacingEntry, setPendingSpacingEntry] = useState(null);

  async function openReleasedReview(entry, stageTitle, scoreTab) {
    if (!entry?.available || !entry.sessionId || releasedReviewLoading) return;
    setReleasedReviewLoading(true);
    setReleasedReviewError("");
    try {
      const response = await fetch(
        `${apiUrl}/evaluation/session/${entry.sessionId}/released-assessment-review`,
        { headers: { Authorization: `Bearer ${auth.user?.token}` } }
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || "RELEASED_ASSESSMENT_REVIEW_LOAD_FAILED");
      setReleasedReviewStageTitle(stageTitle);
      setReleasedReview({ ...body, scoreTab });
    } catch (_error) {
      setReleasedReviewError("The final rating review could not be loaded.");
    } finally {
      setReleasedReviewLoading(false);
    }
  }

  const resolveScopedText = (key, ...fallbacks) =>
    resolveScopedTranslation(scopedT, key, ...fallbacks);

  const resolveProjectText = (key, ...fallbacks) => (
    scopedLocale.isEnabled
      ? resolveScopedText(key, ...fallbacks)
      : (key ? appT(key) : fallbacks.find(Boolean) || "")
  );

  if (releasedReview) {
    return <>
      <ScrollToTopApp dependency={releasedReview.sessionId} />
      <ReleasedAssessmentReview
        onBack={() => setReleasedReview(null)}
        presentation={releasedReview.presentation}
        review={releasedReview}
        stageTitle={releasedReviewStageTitle}
      />
    </>;
  }

  const resolveProgramText = (program, key, ...fallbacks) => {
    const isProgramScoped =
      scopedLocale.isEnabled &&
      scopedLocale.config?.programSlug === program.programSlug;

    if (isProgramScoped) {
      return resolveScopedText(key, ...fallbacks);
    }

    return key ? appT(key) : fallbacks.find(Boolean) || "";
  };

  const scopedLabel = (program, key, options) => {
    const isProgramScoped =
      scopedLocale.isEnabled &&
      scopedLocale.config?.programSlug === program.programSlug;

    return isProgramScoped
      ? scopedT(key, options)
      : appT(key, options);
  };

  // ============================================================
  // HELPERS
  // ============================================================

  function getProgramProgressData(program) {
    return progress?.[program.programSlug] || {
      completedSteps: [],
      completedPhases: [],
      availableDocuments: [],
      steps: [],
      phases: [],
      completionPercentage: 0
    };
  }

  function getAvailableDocuments(program) {
    return getProgramProgressData(program).availableDocuments || [];
  }

  function isStepCompleted(program, step) {
    return readResolvedStepState(step).completed;
  }

  function isStepUnlocked(program, step) {
    return readResolvedStepState(step).unlocked;
  }

  function isStepEligible(program, step) {
    return readResolvedStepState(step).eligible;
  }

  function isStepRepeatable(program, step) {
    return readResolvedStepState(step).repeatable;
  }

  function getPhaseProgress(program, phase) {
    const progressData = getProgramProgressData(program);

    if (program.learnerPresentation?.phaseProgressUnit === "listening_session") {
      const sessionSteps = (progressData.steps || program.steps || []).filter(step =>
        step.phase === phase.key && step.learnerUnit?.type === "listening_session");
      const completed = sessionSteps.filter(step => step.learnerUnit?.presentation?.composite
        ? step.learnerUnit.presentation.composite.completed
        : readResolvedStepState(step).completed).length;
      return { completed, total: sessionSteps.length, percentage: 0, isCompleted: sessionSteps.length > 0 && completed === sessionSteps.length };
    }

    const backendPhase = (progressData.phases || []).find(
      p => p.key === phase.key
    );

    if (backendPhase) {
      return {
        completed: backendPhase.completedSteps || 0,
        total: backendPhase.totalSteps || 0,
        percentage: backendPhase.percentage || 0,
        isCompleted: backendPhase.isCompleted || false
      };
    }

    return {
      completed: 0,
      total: 0,
      percentage: 0,
      isCompleted: false
    };
  }

  function getGlobalProgress(program) {
    const progressData = getProgramProgressData(program);

    if (typeof progressData.completionPercentage === "number") {
      return progressData.completionPercentage;
    }

    return 0;
  }

  function sessionPlanningText(step) {
    const planning = step.learnerUnit?.presentation?.sessionPlanning;
    if (planning?.contractVersion !== "byce-g3-session-planning.v1") return null;

    const parts = [planning.parameterCount === 1
      ? "One rating each"
      : planning.parameterCount === 4
        ? "Four ratings each"
        : `${planning.parameterCount} ratings each`];
    if (planning.includesReflection) parts[0] += " + short reflection";
    if (planning.oneSitting) parts.push("One sitting");
    if (planning.durationStatus === "source_settled" && Number.isInteger(planning.estimatedDurationMinutes)) {
      parts.push(`About ${planning.estimatedDurationMinutes} minutes`);
    }
    return parts.join(" · ");
  }

  function getStepPath(program, step, { review = false } = {}) {
    const projectMode = project?.mode || "dashboard";
    const isProgramScoped =
      scopedLocale.isEnabled &&
      scopedLocale.config?.programSlug === program.programSlug;
    const basePath =
      `/app/project/${slug}/run/${program.programSlug}`;

    if (projectMode !== "dashboard") {
      return isProgramScoped
        ? `${basePath}${scopedLocale.buildSearchWithLocale({ step: null, review: review ? "completed" : null })}`
        : basePath;
    }

    if (isProgramScoped) {
      return `${basePath}${scopedLocale.buildSearchWithLocale({
        step: step.key,
        review: review ? "completed" : null,
      })}`;
    }

    return `${basePath}?step=${step.key}${review ? "&review=completed" : ""}`;
  }

  function getDocumentUrl(program, doc) {
    const isProgramScoped =
      scopedLocale.isEnabled &&
      scopedLocale.config?.programSlug === program.programSlug;
    const params = new URLSearchParams({
      projectId: project._id,
      lang: isProgramScoped ? scopedLocale.documentLocale : "en"
    });

    return [
      `${apiUrl}/programs/${program.programSlug}/documents`,
      `${doc.key}/view?${params.toString()}`
    ].join("/");
  }

  function enterStep(program, step, practiceStage, completed) {
    const completedEntryReview = completed &&
      step.resolvedExecutionPolicy?.completedEntry?.mode === "review";
    const hasCanonicalSameDayEvidence =
      getProgramProgressData(program).practiceDayProjection?.projectionVersion === G3_PRACTICE_DAY_PROJECTION &&
      practiceStage?.currentDay?.hasCompletedParticipatingSession === true;
    const spacingRequirement = practiceStage?.requirement?.minDistinctDays;
    const spacingRequirementMet = Number.isInteger(spacingRequirement) &&
      (practiceStage?.distinctPracticeDays || 0) >= spacingRequirement;
    const isOrdinaryPracticeSession =
      step.learnerUnit?.type === "listening_session" &&
      step.learnerUnit?.practiceDay?.participates === true;
    if (!completed && isOrdinaryPracticeSession && hasCanonicalSameDayEvidence && !spacingRequirementMet) {
      setPendingSpacingEntry({
        path: getStepPath(program, step),
        distinctPracticeDays: practiceStage?.distinctPracticeDays || 0,
      });
      return;
    }
    navigate(getStepPath(program, step, { review: completedEntryReview }));
  }

  // ============================================================
  // RENDER
  // ============================================================

  const projectTitle = resolveProjectText(
    project.titleKey,
    project.title,
    project.name
  );
  const projectDescription = (project.descriptionKey || project.description)
    ? resolveProjectText(project.descriptionKey, project.description)
    : null;
  const showProjectDescription = !programs.some(program =>
    program.learnerPresentation?.hideProjectDescription === true);
  const projectIntroduction = project.uiBlocks?.learnerPresentation?.projectIntroduction || null;
  const projectProvenance = programs.map(program => {
    const title = resolveProgramText(program, program.titleKey || program.labelKey, program.title);
    const description = (program.descriptionKey || program.description)
      ? resolveProgramText(program, program.descriptionKey, program.description)
      : null;
    return program.learnerPresentation?.hideProjectDescription === true &&
      samePresentationFact(title, projectTitle) ? description : null;
  }).find(Boolean) || null;
  const participantContext = projectParticipationContextLabel(project);
  const hasMembershipSummary = Boolean(membership?.participantIdentifier || membership?.group?.name);
  const overviewProgram = programs.length === 1 ? programs[0] : null;
  const overviewProgressData = overviewProgram ? getProgramProgressData(overviewProgram) : null;

  const dashboard = (
    <div
      className={[
        "projects-dashboard",
        !isCatalogLaunch && "app-module"
      ].filter(Boolean).join(" ")}
    >

      {/* HEADER */}
      {!isCatalogLaunch && (
        <section
          className="projects-overview projects-header"
          lang={scopedLocale.isEnabled ? scopedLocale.contentLang : undefined}
        >
          <div className="projects-overview__identity">
            <div className="projects-header__title-row">
              <div>
                {participantContext && (
                  <p className="projects-overview__eyebrow">{participantContext}</p>
                )}
                <h1>
                  {projectTitle}
                </h1>
              </div>
              {effectiveAccess?.facilitateOverview === true && (
                <Link
                  className="av-app-button av-app-button--secondary av-app-button--compact projects-header__facilitate-action"
                  to={`/app/facilitate/project/${slug}`}
                >
                  Facilitate project
                </Link>
              )}
            </div>
            {projectDescription && showProjectDescription && (
              <p className="projects-intro">
                {projectDescription}
              </p>
            )}
            {projectIntroduction && (
              <p className="projects-intro">
                {projectIntroduction}
              </p>
            )}
            {projectProvenance && (
              <p className="projects-header__provenance">
                {projectProvenance}
              </p>
            )}
          </div>
          {overviewProgressData && (
            <LearnerSummary
              isCompleted={overviewProgressData.isProgramCompleted}
              baseGradeResult={overviewProgressData.educationalResults?.baseGrade}
            />
          )}
        </section>
      )}

      {/* ALERTS */}
      {certificationStatus?.status === "processing" && (
        <div className="projects-alert projects-alert--processing">
          ⏳ {appT("generating_certificate")}
        </div>
      )}

      {certificationStatus?.status === "failed" && (
        <div className="projects-alert projects-alert--error">
          ⚠️ {appT("certificate_generation_failed")}
        </div>
      )}

      {/* MEMBERSHIP */}
      {hasMembershipSummary && (
        <div className="projects-membership">
          {membership.participantIdentifier && (
            <p>
              <strong>{appT("participant_id")}:</strong>{" "}
              {membership.participantIdentifier}
            </p>
          )}
          {membership.group?.name && (
            <p>
              <strong>{appT("group")}:</strong>{" "}
              {membership.group.name}
            </p>
          )}
        </div>
      )}

      {/* PROGRAMS */}
      {programs.map(program => {

        const globalProgress = getGlobalProgress(program);
        const progressData = getProgramProgressData(program);
        const baseGradeResult = progressData.educationalResults?.baseGrade;
        const isProgramScoped =
          scopedLocale.isEnabled &&
          scopedLocale.config?.programSlug === program.programSlug;
        const programTitle = resolveProgramText(
          program,
          program.titleKey || program.labelKey,
          program.title
        );
        const programDescription = (program.descriptionKey || program.description)
          ? resolveProgramText(program, program.descriptionKey, program.description)
          : null;
        const showProgramTitle = !samePresentationFact(programTitle, projectTitle);
        const showProgramDescription = !samePresentationFact(programDescription, projectDescription) &&
          !samePresentationFact(programDescription, projectProvenance);
        const showProgramIdentity = showProgramTitle || (programDescription && showProgramDescription);

        return (
          <div
            key={program._id}
            className="projects-program"
            lang={isProgramScoped ? scopedLocale.contentLang : undefined}
          >

            {(showProgramIdentity || (!isCatalogLaunch && isProgramScoped)) && <div className="projects-program-header">
              {showProgramIdentity && <div>
                {showProgramTitle && <h2>{programTitle}</h2>}
                {programDescription && showProgramDescription && <p>{programDescription}</p>}
              </div>}

              {!isCatalogLaunch && isProgramScoped && (
                <ProgramScopedLocaleSelector />
              )}
            </div>}

            {!program.learnerPresentation?.hideGlobalPercentage && <div className="projects-progress">
              <strong>{scopedLabel(program, "progress")}:</strong> {globalProgress}%
            </div>}

            {!overviewProgram && <LearnerSummary
              isCompleted={progressData.isProgramCompleted}
              baseGradeResult={baseGradeResult}
            />}

            {program.phases
              ?.sort((a, b) => a.order - b.order)
              .map(phase => {

                const phaseProgress = getPhaseProgress(program, phase);
                const practiceStage = progressData.practiceDayProjection?.stages?.find(item => item.stageKey === phase.key);
                const phaseSteps = (progressData.steps || program.steps).filter(step => step.phase === phase.key);
                const phaseReached = phaseSteps.some(step => readResolvedStepState(step).unlocked);
                const temporalTokens = temporalCopyTokens(progressData.temporalDelivery, {
                  locale: scopedLocale.contentLang || "en",
                  participantTimeZone: progressData.learnerPresentation?.participantTimeZone,
                  resultRelease: progressData.learnerPresentation?.stage4ResultRelease,
                });
                const phaseIntroduction = progressData.learnerPresentation?.phases?.[phase.key];
                const phaseIntroductionText = (phaseReached || phase.key === "stage-4") &&
                  phaseIntroduction?.copyContractId === "building-your-clinical-ear-g3-punchlist-19-copy@1"
                  ? renderExactTokenizedCopy(phaseIntroduction.template, temporalTokens)
                  : null;
                const phaseSessionContextText = phaseReached &&
                  phaseIntroduction?.sessionContext?.copyContractId === "building-your-clinical-ear-g3-punchlist-19-copy@1"
                  ? renderExactTokenizedCopy(phaseIntroduction.sessionContext.template, temporalTokens)
                  : null;
                const stage4Window = phase.key === "stage-4"
                  ? progressData.learnerPresentation?.stage4Window
                  : null;
                const stage4WindowState = progressData.temporalDelivery?.windows?.stage4?.state;
                const stage4WindowTemplate = stage4Window?.template?.[stage4WindowState];
                const stage4WindowText = stage4WindowTemplate
                  ? renderExactTokenizedCopy(stage4WindowTemplate, temporalTokens)
                  : null;
                const stage4Completed = phase.key === "stage-4" && phaseSteps.some(step => step.completed || step.isCompleted);
                const stage4Pending = progressData.learnerPresentation?.stage4Pending;
                const stage4PendingText = stage4Completed &&
                  progressData.learnerPresentation?.stage4ReleasedReview?.state === "pending_release"
                  ? renderExactTokenizedCopy(stage4Pending?.template, temporalTokens)
                  : null;
                const stage4ReleasedReview = phase.key === "stage-4"
                  ? progressData.learnerPresentation?.stage4ReleasedReview
                  : null;
                const stage4ScorePresentation = phase.key === "stage-4"
                  ? progressData.learnerPresentation?.stage4Score
                  : null;
                const stage4FinalRatingResult = phase.key === "stage-4"
                  ? progressData.educationalResults?.finalRating
                  : null;
                const stage4ScoreTab = stage4ScorePresentation &&
                  stage4FinalRatingResult?.availability === "available"
                  ? {
                    available: true,
                    presentation: stage4ScorePresentation,
                    result: stage4FinalRatingResult,
                  }
                  : { available: false };

                const isTrainingPhase = phase.key === "training";

                const allDocs = getAvailableDocuments(program);

                const visibleDocuments = allDocs.filter(doc => {

                  // 🔥 progress report → només el de la fase actual
                  if (doc.documentType === "progress_report") {
                    return doc.afterPhase === phase.key;
                  }

                  // 🔹 activity / final → només si fase completada (excepte training)
                  if (doc.afterPhase === phase.key) {
                    return phaseProgress.isCompleted && !isTrainingPhase;
                  }

                  return false;
                });

                return (
                  <div key={phase.key} className="projects-phase">

                    <h3>
                      {resolveProgramText(
                        program,
                        phase.titleKey || phase.labelKey,
                        phase.key
                      )}
                    </h3>

                    {phase.descriptionKey && (
                      <div className="projects-phase-description">
                        <p>{resolveProgramText(program, phase.descriptionKey)}</p>
                      </div>
                    )}

                    <ExactCopyBlock blockId={phaseIntroduction?.blockId} text={phaseIntroductionText} />
                    <ExactCopyBlock blockId={stage4Window?.blockId} text={stage4WindowText} />
                    <ExactCopyBlock blockId={stage4Pending?.blockId} text={stage4PendingText} />
                    {releasedReviewError && <p role="alert">{releasedReviewError}</p>}

                    {!isTrainingPhase && program.learnerPresentation?.phaseProgressUnit === "listening_session" && (
                      <div className="projects-phase-progress">
                        <p>{phaseProgress.completed} of {phaseProgress.total} listening sessions completed</p>
                        {practiceStage?.missingCalendarContext
                          ? <p>{`Practice days: calendar context unavailable for ${practiceStage.missingCalendarContext} completed ${practiceStage.missingCalendarContext === 1 ? "session" : "sessions"}`}</p>
                          : progressData.practiceDayProjection?.projectionVersion === G3_PRACTICE_DAY_PROJECTION
                          ? <SpacingProgress phaseCompleted={phaseProgress.isCompleted} stage={practiceStage} />
                          : practiceStage?.completedSessions > 0
                            ? <p>{`Practice days: ${practiceStage.distinctPracticeDays} of ${practiceStage.requirement.minDistinctDays} required${practiceStage.daysNeeded > 0 ? ` · ${practiceStage.daysNeeded} more separate practice ${practiceStage.daysNeeded === 1 ? "day" : "days"} needed` : ""}`}</p>
                            : null}
                      </div>
                    )}
                    <ExactCopyBlock
                      blockId={phaseIntroduction?.sessionContext?.blockId}
                      text={phaseSessionContextText}
                    />
                    {!isTrainingPhase && program.learnerPresentation?.phaseProgressUnit !== "listening_session" && (
                      <p className="projects-phase-progress">
                        {phaseProgress.completed} / {phaseProgress.total} · {phaseProgress.percentage}%
                      </p>
                    )}

                    {(progressData.steps || program.steps)
                      .filter(step => step.phase === phase.key)
                      .filter(step => !step.learnerUnit?.presentation?.composite?.hiddenAsStandaloneCard)
                      .sort((a, b) => a.order - b.order)
                      .map(step => {

                        const composite = step.learnerUnit?.presentation?.composite;
                        const entryStep = composite
                          ? (progressData.steps || program.steps).find(candidate => candidate.key === composite.entryStepKey) || step
                          : step;
                        const completed = composite?.completed ?? isStepCompleted(program, step);
                        const unlocked = isStepUnlocked(program, entryStep);
                        const eligible = isStepEligible(program, entryStep);
                        const repeatable = isStepRepeatable(program, entryStep);
                        const completedEntryReview = completed &&
                          entryStep.resolvedExecutionPolicy?.completedEntry?.mode === "review";
                        const isTraining = step.countsForCompletion === false;
                        const denialPresentation = readResolvedStepDenialPresentation(step);
                        const temporalDenialPresentation = denialPresentation?.reasonCode ===
                          "PROGRAM_STEP_TEMPORAL_ENTRY_BLOCKED" ? denialPresentation : null;
                        const canEnter = completedEntryReview ? unlocked : eligible && (!completed || repeatable);
                        const isStage4FinalRating = phase.key === "stage-4" &&
                          step.key === "g3-stage-4-final-rating";
                        const planningText = sessionPlanningText(step);
                        const sessionOpening = program.learnerPresentation?.listeningSessionPlacement
                          ?.sessionOpening?.placement === "session-card"
                          ? step.learnerUnit?.presentation?.opening
                          : null;

                        return (
                          <div
                            key={step.key}
                            className={[
                              "projects-step",
                              completed && !isTraining ? "projects-step--completed" : "",
                              !unlocked ? "projects-step--locked" : "",
                              temporalDenialPresentation ? "projects-step--unavailable" : ""
                            ].join(" ")}
                            aria-disabled={!canEnter}
                            onClick={canEnter ? () => enterStep(program, entryStep, practiceStage, completed) : undefined}
                          >

                            <div className="projects-step-title">
                              {resolveProgramText(
                                program,
                                step.titleKey || step.labelKey,
                                step.key
                              )}

                              {completed && !isTraining && (
                                <span className="projects-step-status">
                                  <span aria-hidden="true">✓</span>
                                  <span aria-hidden="true">—</span>
                                  <span>Completed</span>
                                </span>
                              )}
                            </div>

                            {!isTraining && step.progress?.totalTrials > 0 && (
                              <div className="projects-step-meta">
                                {step.progress.completedTrials} / {step.progress.totalTrials} {program.learnerPresentation?.contractVersion === "stage_listening_sessions.v1" ? "Voices" : scopedLabel(program, "samples")}
                                {composite?.status === "reflection_pending" ? " · Reflection pending" : composite?.status === "completed" ? " · Reflection completed" : ""}
                              </div>
                            )}

                            {planningText && (
                              <div className="projects-step-meta projects-step-planning">
                                {planningText}
                              </div>
                            )}

                            <ExactCopyBlock
                              blockId={sessionOpening?.blockId}
                              text={sessionOpening?.template}
                            />

                            {isStage4FinalRating && stage4ReleasedReview?.available && (
                              <div className="projects-step-final-result">
                                {stage4ScoreTab.available && <div className="projects-step-final-score">
                                  <span>Final rating score</span>
                                  <strong>{formatScorePoints(stage4FinalRatingResult.points)} <span>/16</span></strong>
                                </div>}
                                <div className="projects-step-action">
                                  <button
                                    type="button"
                                    className="app-btn app-btn-primary"
                                    disabled={releasedReviewLoading}
                                    onClick={event => {
                                      event.stopPropagation();
                                      openReleasedReview(stage4ReleasedReview, resolveProgramText(
                                        program, phase.titleKey || phase.labelKey, phase.key
                                      ), stage4ScoreTab);
                                    }}
                                  >
                                    {releasedReviewLoading ? "Loading review…" : "Review final rating"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {isTraining && step.progress?.completedTrials > 0 && (
                              <div className="projects-step-meta projects-step-meta--training">
                                ({step.progress.completedTrials} {step.progress.completedTrials === 1 ? scopedLabel(program, "response") : scopedLabel(program, "responses")})
                              </div>
                            )}

                            {temporalDenialPresentation && (
                              <div className="projects-step-meta projects-step-meta--unavailable">
                                {temporalDenialPresentation.title}
                              </div>
                            )}

                          </div>
                        );
                      })}

                    {visibleDocuments.length > 0 && (
                      <div className="projects-documents">
                        <p><strong>{scopedLabel(program, "available_reports")}</strong></p>

                        {visibleDocuments.map(doc => (
                          <div key={doc.key}>
                            <button
                              className="app-btn-link"
                              onClick={() =>
                                openProtectedPdf({
                                  url: getDocumentUrl(program, doc),
                                  token: auth.user?.token
                                })
                              }
                            >
                              {resolveProgramText(
                                program,
                                doc.titleKey,
                                scopedLabel(program, "view_report")
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {phase.completionMessageKey && phaseProgress.isCompleted && (
                      <div className="projects-phase-message">
                        <p>
                          {resolveProgramText(
                            program,
                            phase.completionMessageKey
                          )}
                        </p>
                      </div>
                    )}

                  </div>
                );
              })}
          </div>
        );
      })}

      {/* ETHICS MODAL */}
      {showEthicsModal && project?.uiBlocks?.ethics && (
        <BaseModalApp
          isOpen={showEthicsModal}
          onRequestClose={handleAcceptEthics}
          contentLabel={resolveProjectText(
            project.uiBlocks.ethics.titleKey,
            "Ethics"
          )}
          size="500px"
        >
          <div
            className="projects-modal"
            lang={scopedLocale.isEnabled ? scopedLocale.contentLang : undefined}
          >

            <h2>
              {resolveProjectText(project.uiBlocks.ethics.titleKey)}
            </h2>

            <p>{resolveProjectText(project.uiBlocks.ethics.textKey)}</p>

            <div className="projects-modal-actions">
              <button
                className="app-btn app-btn-primary"
                onClick={handleAcceptEthics}
              >
                {resolveProjectText(project.uiBlocks.ethics.buttonKey)}
              </button>
            </div>

          </div>
        </BaseModalApp>
      )}

      <BaseModalApp
        contentLabel="Same-day listening reminder"
        isOpen={Boolean(pendingSpacingEntry)}
        onRequestClose={() => setPendingSpacingEntry(null)}
        size="500px"
      >
        <div className="projects-modal">
          <h2>Before you continue</h2>
          <p>{sameDayWarning(pendingSpacingEntry?.distinctPracticeDays || 0)}</p>
          <div className="projects-modal-actions">
            <button className="app-btn app-btn-secondary" onClick={() => setPendingSpacingEntry(null)} type="button">Cancel</button>
            <button
              className="app-btn app-btn-primary"
              onClick={() => {
                const path = pendingSpacingEntry?.path;
                setPendingSpacingEntry(null);
                if (path) navigate(path);
              }}
              type="button"
            >
              Continue
            </button>
          </div>
        </div>
      </BaseModalApp>

    </div>
  );

  const catalogLaunchContextControls =
    isCatalogLaunch && scopedLocale.isEnabled
      ? (
        <ProgramScopedLocaleSelector
          id="catalog-launch-program-locale"
        />
      )
      : null;

  return isCatalogLaunch
    ? (
      <CatalogLaunchShell contextControls={catalogLaunchContextControls}>
        {dashboard}
      </CatalogLaunchShell>
    )
    : dashboard;
}
